import { SITE_NAME, SITE_URL, DEFAULT_OG_IMAGE, buildCanonicalUrl } from "./config";
import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  buildFoodEstablishmentSchema,
  buildLiquorStoreSchema,
  buildBreadcrumbSchema,
} from "./structuredData";

/** "fast-food" / "red-wine" -> "Fast Food" / "Red Wine". Empty/undefined -> "". */
function humanize(value) {
  if (!value) return "";
  return String(value)
    .replace(/[-_]+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function buildStoreDescription(name, categoryLabel, ratingStats) {
  let description = `Order from ${name}${
    categoryLabel ? ` (${categoryLabel})` : ""
  } on ${SITE_NAME} and get it delivered fast.`;
  if (ratingStats?.total_no_ratings > 0) {
    description += ` Rated ${ratingStats.average_rating.toFixed(1)}/5 by ${
      ratingStats.total_no_ratings
    } customers.`;
  }
  return description;
}

/** Homepage — the store-discovery listing. Organization/WebSite JSON-LD is
 * only emitted here, never duplicated on other pages. */
export function generateHomeSEO() {
  return {
    title: `${SITE_NAME} – Order Food Online in Kenya`,
    description:
      "Teco connects you with local restaurants across Kenya. Browse menus, order online, and get fast, reliable delivery to your door.",
    canonicalPath: "/",
    robots: "index, follow",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    jsonLd: [buildOrganizationSchema(), buildWebSiteSchema()],
  };
}

/**
 * Flat marketing/info pages (About, Contact, Privacy, Terms, Safety
 * Guidelines) — each call site supplies its own real title/description.
 * @param {{title: string, description: string, path: string}} params
 */
export function generateStaticPageSEO({ title, description, path }) {
  return {
    title: `${title} | ${SITE_NAME}`,
    description,
    canonicalPath: path,
    robots: "index, follow",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    jsonLd: [],
  };
}

/**
 * A food store's page (StoreInfo.jsx). Only called once real store data has
 * loaded — see generateNoIndexSEO for the loading/error fallback.
 * @param {string} storeId
 * @param {Object} storeInfo - from useStoreInfo (store_name, store_category, imgSrc, bannerImgSrc)
 * @param {{average_rating: number, total_no_ratings: number}} [ratingStats]
 */
export function generateStoreSEO(storeId, storeInfo, ratingStats) {
  const name = storeInfo?.store_name || "Restaurant";
  const categoryLabel = humanize(storeInfo?.store_category);
  const image = storeInfo?.bannerImgSrc || storeInfo?.imgSrc || undefined;
  const canonicalPath = `/stores/${storeId}`;
  const url = buildCanonicalUrl(canonicalPath);

  const title = categoryLabel
    ? `${name} – ${categoryLabel} Delivery | ${SITE_NAME}`
    : `${name} – Order Food Online | ${SITE_NAME}`;

  const breadcrumbItems = [
    { name: "Home", url: SITE_URL },
    { name, url },
  ];

  return {
    title,
    description: buildStoreDescription(name, categoryLabel, ratingStats),
    canonicalPath,
    robots: "index, follow",
    ogType: "website",
    ogImage: image || DEFAULT_OG_IMAGE,
    breadcrumbItems,
    jsonLd: [
      buildFoodEstablishmentSchema({ name, url, image, cuisine: categoryLabel, ratingStats }),
      buildBreadcrumbSchema(breadcrumbItems),
    ],
  };
}

/**
 * A liquor store's page (LiquorStoreInfo.jsx). Same shape as
 * generateStoreSEO but with a LiquorStore schema type and no `cuisine`.
 * Note: liquor discovery is currently dormant in the UI (the Liquor tab is
 * commented out of TabContext.tsx) — these routes are still reachable by
 * direct URL, so they still get real SEO rather than being left broken.
 */
export function generateLiquorStoreSEO(storeId, storeInfo, ratingStats) {
  const name = storeInfo?.store_name || "Liquor Store";
  const image = storeInfo?.bannerImgSrc || storeInfo?.imgSrc || undefined;
  const canonicalPath = `/stores/liquor/${storeId}`;
  const url = buildCanonicalUrl(canonicalPath);

  const breadcrumbItems = [
    { name: "Home", url: SITE_URL },
    { name, url },
  ];

  return {
    title: `${name} – Liquor Delivery | ${SITE_NAME}`,
    description: buildStoreDescription(name, "Liquor Store", ratingStats),
    canonicalPath,
    robots: "index, follow",
    ogType: "website",
    ogImage: image || DEFAULT_OG_IMAGE,
    breadcrumbItems,
    jsonLd: [
      buildLiquorStoreSchema({ name, url, image, ratingStats }),
      buildBreadcrumbSchema(breadcrumbItems),
    ],
  };
}

/**
 * A liquor store's type/subType catalog page (LiquorStoreProducts.jsx),
 * e.g. /stores/liquor/:storeId/wine/red-wine.
 */
export function generateLiquorCategorySEO(storeId, storeName, type, subType) {
  const name = storeName || "Liquor Store";
  const typeLabel = humanize(type);
  const subTypeLabel = humanize(subType);
  const categoryLabel = [subTypeLabel, typeLabel].filter(Boolean).join(" ");
  const storeUrl = buildCanonicalUrl(`/stores/liquor/${storeId}`);
  const canonicalPath = `/stores/liquor/${storeId}/${type}/${subType}`;
  const url = buildCanonicalUrl(canonicalPath);

  const breadcrumbItems = [
    { name: "Home", url: SITE_URL },
    { name, url: storeUrl },
    { name: categoryLabel || "Products", url },
  ];

  return {
    title: `${categoryLabel} at ${name} | ${SITE_NAME}`,
    description: `Shop ${categoryLabel || "products"} at ${name} on ${SITE_NAME}.`,
    canonicalPath,
    robots: "index, follow",
    ogType: "website",
    ogImage: DEFAULT_OG_IMAGE,
    breadcrumbItems,
    jsonLd: [buildBreadcrumbSchema(breadcrumbItems)],
  };
}

/**
 * Fallback for every private, transactional, authenticated, or utility
 * page — and for a dynamic page's loading/error state, before real data
 * exists to build a proper description from. Still sets a real
 * document.title (prevents the previous route's title/meta from bleeding
 * into this one on client-side navigation).
 * @param {string} title - human page name, e.g. "Checkout", "Your Orders"
 */
export function generateNoIndexSEO(title) {
  return {
    title: `${title} | ${SITE_NAME}`,
    description: undefined,
    canonicalPath: undefined,
    robots: "noindex, nofollow",
    ogType: undefined,
    ogImage: undefined,
    jsonLd: [],
  };
}
